import React, {useMemo, useState} from "react";
import {db} from "../config/firebase-config";
import {addDoc, collection, serverTimestamp} from "firebase/firestore";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {Container} from "react-bootstrap";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Dropdown from "react-bootstrap/Dropdown";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import {benachrichtigeMailserver} from "../utils/mailserver";
import {
  EMAIL_HINWEIS,
  getrimmteEmailAdresse,
  istGueltigeEmailAdresse,
} from "../utils/emailAdresse";

/**
 * Immer deutsch formatiert, nie über die Locale des Geräts: genau daran ist der
 * Mailversand 14 Monate lang gescheitert (#3).
 */
const alsMeldezeitpunkt = (datum) =>
  datum.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Die Kerndaten des Mangels — im Fehlerzustand der Beleg, dass er gespeichert
 * ist, und die Referenz, die der Melder am Telefon vorliest. Die eigenen
 * Kontaktdaten des Melders stehen nicht dabei: er kennt sie. Die
 * Firestore-Dokument-ID auch nicht — für die Technik nicht nachschlagbar,
 * kryptisch beim Diktieren.
 */
const kerndaten = ({mangel, kennzeichen, gemeldetAm}) => ({
  kennzeichen,
  kategorie: mangel.kategorie,
  kilometer: mangel.kilometer,
  gemeldet: alsMeldezeitpunkt(gemeldetAm),
  beschreibung: mangel.historie[0].beschreibung,
});

/**
 * Die Technik-Hotline: fest im Code statt aus der Konfiguration, weil sie eine
 * fachliche Zusage ist und keine Umgebungsentscheidung — ein fehlender
 * Konfigurationswert dürfte hier keine leere Zeile erzeugen.
 */
const TECHNIK_HOTLINE = {
  anzeige: "0421-36111700",
  waehlbar: "tel:+4942136111700",
};

/**
 * Die Adresse der Technik, aus demselben Grund fest im Code wie die Hotline.
 * Sie stand früher in der Konfiguration, weil das Frontend sie auch als
 * Empfänger mitschickte; den Empfänger bestimmt inzwischen der Mailserver.
 */
const TECHNIK_EMAIL = "technik@rettungsdienstbremen.de";

/**
 * Der öffentliche Meldedialog. Er kennt Fahrzeuge nur als Meldeziele
 * (`{id, kennzeichen}`) und darf jeder melden, angemeldet oder nicht.
 */
const ModalMangel = (props) => {
  const today = new Date();

  const [kategorie, setKategorie] = useState("");
  const [kontaktVorname, setKontaktVorname] = useState("");
  const [kontaktNachname, setKontaktNachname] = useState("");
  const [kontaktEmail, setKontaktEmail] = useState("");
  const [kontaktTelefon, setKontaktTelefon] = useState("");
  const [kilometer, setKilometer] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [fahrzeugId, setFahrzeugId] = useState("");
  const [checkDatenschutz, setCheckDatenschutz] = useState(false);
  // Erst wenn der Melder das E-Mail-Feld einmal verlassen hat, darf der Hinweis
  // erscheinen — sonst wäre jede halbfertige Eingabe rot.
  const [emailBeruehrt, setEmailBeruehrt] = useState(false);
  const [istAmSenden, setIstAmSenden] = useState(false);
  // Gesetzt, sobald der Mangel steht, die Benachrichtigung aber nicht raus ist:
  // trägt den Stand der Meldung in den Fehlerzustand und schaltet ihn ein.
  const [gescheiterteMeldung, setGescheiterteMeldung] = useState(null);

  const sortierteMeldeziele = useMemo(
    () =>
      [...props.meldeziele].sort((a, b) =>
        a.kennzeichen > b.kennzeichen
          ? 1
          : b.kennzeichen > a.kennzeichen
            ? -1
            : 0,
      ),
    [props.meldeziele],
  );

  // Das Kennzeichen folgt zwingend aus der Auswahl — abgeleitet statt über
  // einen Effekt in einen Zustand gespiegelt, damit es nie hinterherhinkt.
  const gewaehltesMeldeziel = props.meldeziele.find(
    (eintrag) => eintrag.id === fahrzeugId,
  );
  const kennzeichen = gewaehltesMeldeziel ? gewaehltesMeldeziel.kennzeichen : "";

  const emailAdresse = getrimmteEmailAdresse(kontaktEmail);
  const emailIstGueltig = istGueltigeEmailAdresse(kontaktEmail);
  const emailHinweisSichtbar = emailBeruehrt && !emailIstGueltig;

  /**
   * Die einzige Stelle, die über die Absende-Sperre entscheidet. Vorher lag sie
   * in zwei nebeneinanderstehenden Effekten: einer prüfte alle Pflichtfelder,
   * der andere gab allein anhand der Kennzeichen-Auswahl frei — und beide
   * schrieben in denselben Zustand. Welcher gewann, hing an der Bedienreihen-
   * folge. Abgeleitet statt in einen Zustand geschrieben, damit sich nichts
   * mehr gegenseitig überschreiben kann.
   */
  const cannotSave = !(
    kontaktVorname !== "" &&
    kontaktNachname !== "" &&
    fahrzeugId !== "" &&
    kilometer !== "" &&
    emailIstGueltig &&
    kategorie !== "" &&
    beschreibung !== "" &&
    checkDatenschutz === true
  );

  const changeKm = (e) => {
    let valueTmp = e.target.value;

    const numericString = valueTmp.replace(/\D/g, "");

    // Wenn der String leer ist, gib eine leere Zeichenkette zurück
    if (numericString === "") return "";

    // Wandelt die Zahl in einen Integer um
    const number = parseInt(numericString, 10);

    setKilometer(number.toLocaleString("de-DE"));
  };

  const handleSave = async () => {
    // Dieselbe Sperre, die den Button ausgraut — hier noch einmal, damit sie
    // nicht allein an einem `disabled`-Attribut hängt.
    if (cannotSave || istAmSenden) {
      return;
    }

    // console.log("save new Mangel mit id", fahrzeugId);

    const path = "fahrzeuge/" + fahrzeugId + "/maengel";
    const docRef = collection(db, path);
    // const docRef = doc(db, "fahrzeuge");

    // `datumAngelegt` setzt der Server: eine falsch gehende Geräteuhr darf die
    // Meldung nicht scheitern lassen.
    let data = {
      new: true,
      status: "offen",
      datumAngelegt: serverTimestamp(),
      datumLastUpdate: today,
      kategorie: kategorie,
      kontaktName: kontaktVorname + " " + kontaktNachname,
      kontaktEmail: emailAdresse,
      kontaktTelefon: kontaktTelefon,
      priorität: 4,
      kilometer: kilometer,
      historie: [
        {
          beschreibung: beschreibung,
          nutzerkreis: "",
          datum: today,
        },
      ],
    };

    // Der Dialog bleibt offen, bis Firestore-Write, Protokoll-Eintrag und
    // Mailversand durch sind: "geschlossen" heißt ab hier auch "erledigt".
    setIstAmSenden(true);

    let mangelId;
    try {
      const doc = await addDoc(docRef, data);
      mangelId = doc.id;
      await writeToProtokoll(mangelId);
    } catch (error) {
      // Scheitert schon das Anlegen, gibt es nichts zu benachrichtigen. Dieser
      // Fall bleibt vorerst wie bisher: der Dialog schließt, der Melder erfährt
      // nichts — sichtbar wird nur der Mailfehler (#14).
      console.error("Mangel konnte nicht angelegt werden", error);
      setIstAmSenden(false);
      handleClose();
      return;
    }

    // Fahrzeug und Kennzeichen wandern hier mit in die Meldung, statt später
    // erneut aus dem State gelesen zu werden: das Formular bleibt im
    // Fehlerzustand bedienbar, und eine nachträglich gewechselte Auswahl darf
    // die angezeigten Kerndaten nicht auf ein Fahrzeug umschreiben, zu dem der
    // gespeicherte Mangel nicht gehört.
    await benachrichtigeTechnik({
      fahrzeugId: fahrzeugId,
      mangelId: mangelId,
      mangel: data,
      kennzeichen: kennzeichen,
      gemeldetAm: today,
    });
  };

  /**
   * Verschickt die Benachrichtigung — und nur die. Der Mangel ist an dieser
   * Stelle bereits geschrieben, ein Wiederholungsversuch fasst ihn nie wieder
   * an, sonst entstünden Duplikate im Mängel-Bestand.
   */
  const benachrichtigeTechnik = async (meldung) => {
    setIstAmSenden(true);

    try {
      await sendeMitEinemWiederholungsversuch(meldung);
      setIstAmSenden(false);
      handleClose();
    } catch (error) {
      console.error("Benachrichtigung konnte nicht versendet werden", error);
      setIstAmSenden(false);
      setGescheiterteMeldung(meldung);
    }
  };

  /**
   * Ein zweiter Versuch nur dort, wo er etwas ändern kann: Funkloch, Timeout,
   * kurzer Serverneustart. Bei 400 und 401 scheitert er identisch — dann lieber
   * sofort in den Fehlerzustand, statt den Melder warten zu lassen.
   */
  const sendeMitEinemWiederholungsversuch = async (meldung) => {
    try {
      await benachrichtigeMailserver(meldung);
    } catch (error) {
      if (!istWiederholbar(error)) {
        throw error;
      }
      await benachrichtigeMailserver(meldung);
    }
  };

  const istWiederholbar = (error) => {
    const status = error?.response?.status;

    if (status !== undefined) {
      return status >= 500;
    }

    // Anfrage raus, keine Antwort zurück: Funkloch oder Timeout. Fehlt auch die
    // Anfrage, war es kein Transportproblem, sondern ein Fehler im Client —
    // den zu wiederholen bringt nichts.
    return error?.request !== undefined;
  };

  const writeToProtokoll = (id) => {
    const path = "fahrzeuge/" + fahrzeugId + "/protokoll";
    const docRef = collection(db, path);

    let data = {
      status: "neu",
      kategorie: kategorie,
      kontaktName: kontaktVorname + " " + kontaktNachname,
      priorität: 4,
      kilometer: kilometer,
      beschreibung: beschreibung,
      nutzerkreis: "",
      datum: today,
      mangelId: id,
    };

    return addDoc(docRef, data);
  };

  const handleClose = () => {
    setFahrzeugId("");
    setGescheiterteMeldung(null);
    setEmailBeruehrt(false);
    props.close();
  };

  const handleCheckDatenschutz = (e) => {
    let result = false;
    if (e.target.value === false || e.target.value === "false") {
      result = true;
    } else {
      result = false;
    }

    setCheckDatenschutz(result);
  };

  // Was der Melder im Fehlerzustand liest: am Telefon vorlesbar, in eine
  // selbst geschriebene Mail kopierbar, und zugleich der Beleg, dass der
  // Mangel wirklich gespeichert ist.
  const fehlerKerndaten = gescheiterteMeldung
    ? kerndaten(gescheiterteMeldung)
    : null;

  return (
    <>
      {/*
        Während des Sendens sind alle Schließwege dicht — Kreuz, Escape und
        Klick auf den Hintergrund genauso wie die beiden Buttons. Sonst wäre der
        Dialog trotz laufendem Speichern wieder weg.
      */}
      <Modal
        show={props.showModal}
        onHide={handleClose}
        backdrop={istAmSenden ? "static" : true}
        keyboard={!istAmSenden}
      >
        <Modal.Header closeButton={!istAmSenden}>
          <Modal.Title>Mangel an Fahrzeug melden</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Container>
            <Form>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Kennzeichen</Form.Label>
                    <Form.Select
                      name="modalstatus"
                      dataLiveSearch
                      onChange={(e) => {
                        setFahrzeugId(e.target.value);
                      }}
                    >
                      <option value={""}>Bitte wählen...</option>
                      {sortierteMeldeziele.map((item) => {
                        return (
                          <option key={item.id} value={item.id}>
                            {item.kennzeichen}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Kilometerstand</Form.Label>
                    <Form.Control
                      type="text"
                      id="inputKilomter"
                      value={kilometer}
                      onChange={(e) => {
                        changeKm(e);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Vorname</Form.Label>
                    <Form.Control
                      type="text"
                      id="inputVorname"
                      value={kontaktVorname}
                      onChange={(e) => {
                        setKontaktVorname(e.target.value);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Nachname</Form.Label>
                    <Form.Control
                      type="text"
                      id="inputNachname"
                      value={kontaktNachname}
                      onChange={(e) => {
                        setKontaktNachname(e.target.value);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>E-Mail</Form.Label>
                    {/*
                      `type="email"` allein wegen der Bildschirmtastatur auf
                      Mobilgeräten. Die browsereigene Prüfung übernimmt hier
                      nichts: der Dialog löst kein natives Absenden aus, und
                      ihre Regel wäre schwächer — sie ließe Adressen ohne Punkt
                      durch.
                    */}
                    <Form.Control
                      type="email"
                      id="inputEmail"
                      value={kontaktEmail}
                      onChange={(e) => {
                        setKontaktEmail(e.target.value);
                      }}
                      onBlur={() => {
                        setEmailBeruehrt(true);
                      }}
                      isInvalid={emailHinweisSichtbar}
                    />
                    {/*
                      Der Hinweis steht nur im Dokument, solange er gilt.
                      Bootstrap blendet ihn sonst per CSS aus — für einen
                      Screenreader bliebe er damit trotzdem lesbar.
                    */}
                    {emailHinweisSichtbar && (
                      <Form.Control.Feedback type="invalid">
                        {EMAIL_HINWEIS}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefon</Form.Label>
                    <Form.Control
                      type="text"
                      id="inputTelefon"
                      value={kontaktTelefon}
                      onChange={(e) => {
                        setKontaktTelefon(e.target.value);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Kategorie des Mangels</Form.Label>
                    <Form.Select
                      name="modalstatus"
                      value={kategorie}
                      onChange={(e) => {
                        setKategorie(e.target.value);
                      }}
                    >
                      <option value="">Bitte wählen...</option>
                      {props.kategorien.map((kategorie) => {
                        return (
                          <option
                            value={kategorie.bezeichnung}
                            key={kategorie.id}
                          >
                            {kategorie.bezeichnung}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Mangelbeschreibung</Form.Label>
                    <Form.Control
                      as="textarea"
                      style={{height: "150px"}}
                      onChange={(e) => {
                        setBeschreibung(e.target.value);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`default-checkbox`}
                    checked={checkDatenschutz}
                    value={checkDatenschutz}
                    onChange={(e) => {
                      handleCheckDatenschutz(e);
                    }}
                  />{" "}
                  Ich habe die{" "}
                  <a href="/datenschutz.html" target="_blank">
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und akzeptiert.
                </Col>
              </Row>
            </Form>
          </Container>
        </Modal.Body>

        {/*
          Im Fehlerzustand bleibt der Dialog offen und zeigt den Fehlschlag
          inline — der Melder hat das Gerät noch in der Hand. "Meldung
          abschicken" verschwindet dabei: der Mangel ist längst geschrieben, ein
          zweites Abschicken würde ihn nur doppeln. Was bleibt, sind die beiden
          Wege zur Technik im Alert — und das Schließen als einziger Ausgang.
        */}
        <Modal.Footer className="flex-column align-items-stretch">
          {gescheiterteMeldung && !istAmSenden && (
            <Alert variant="danger" className="mb-0">
              <Alert.Heading as="h5">
                Mangel gespeichert — Technik nicht benachrichtigt
              </Alert.Heading>
              <p>
                Der Mangel wurde gespeichert und geht nicht verloren. Die
                automatische Benachrichtigung der Technik konnte jedoch nicht
                zugestellt werden.
              </p>
              <p className="fw-bold">
                Bitte den Mangel zusätzlich direkt an die Technik melden — sonst
                bleibt er dort unbemerkt.
              </p>
              {/*
                Der Eskalationsblock nennt zwei Kanäle, die auf jedem Gerät
                ohne Vorbedingung funktionieren — und keinen, der stillschweigend
                versagen kann. Der frühere Button, der eine vorbefüllte Mail im
                lokalen Mailprogramm öffnete, ist ersatzlos weg: auf einem Handy
                ohne eingerichtetes Mailkonto tat er beim Antippen sichtbar
                nichts, und der Melder hatte keinen Grund anzunehmen, dass er
                noch etwas tun muss. Telefon zuerst, weil es am Fahrzeug der
                schnellere und realistischere Weg ist.

                Die Nummer ist zugleich der Linktext — dadurch bleibt sie
                ablesbar, auch wo `tel:` nichts bewirkt. Sie steht bewusst ohne
                Besetzzeiten da: dass sie außerhalb der Erreichbarkeit ins Leere
                führen kann, hat der PO in Kenntnis dieser Warnung entschieden
                und damit die Streichung des Telefonkanals vom 03.08.2026
                zurückgenommen (#19).
              */}
              <div className="border-top border-bottom py-2 mb-3 text-center">
                <a
                  className="d-block fs-4 fw-bold"
                  href={TECHNIK_HOTLINE.waehlbar}
                >
                  {TECHNIK_HOTLINE.anzeige}
                </a>
                <span className="fw-bold">{TECHNIK_EMAIL}</span>
              </div>
              <p className="mb-1 small">
                Fahrzeug: {fehlerKerndaten.kennzeichen} · Kategorie:{" "}
                {fehlerKerndaten.kategorie} · Kilometerstand:{" "}
                {fehlerKerndaten.kilometer} · Gemeldet:{" "}
                {fehlerKerndaten.gemeldet}
              </p>
              {/*
                Eigene Zeile statt angehängt an die Aufzählung darüber: eine
                Beschreibung kann beliebig lang sein und machte die mit
                Trennzeichen aufgereihte Zeile sonst unlesbar.
              */}
              <p className="mb-0 small">
                Beschreibung: {fehlerKerndaten.beschreibung}
              </p>
            </Alert>
          )}
          <div className="d-flex align-items-center justify-content-end gap-2">
            {istAmSenden && (
              <div className="me-auto d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" role="status" />
                <span>
                  Mangel wird gespeichert und die Technik benachrichtigt …
                </span>
              </div>
            )}
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={istAmSenden}
            >
              {/*
                Im Fehlerzustand gibt es nichts mehr abzubrechen — der Mangel
                ist geschrieben. "Abbrechen" würde dort das Gegenteil dessen
                nahelegen, was der Text gerade zusichert.
              */}
              {gescheiterteMeldung ? "Schließen" : "Abbrechen"}
            </Button>
            {!gescheiterteMeldung && (
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={cannotSave || istAmSenden}
              >
                Meldung abschicken
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalMangel;
